import { Box, Divider, Typography } from "@mui/material";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { AppAutocomplete, AppTextField } from "../form";
import { AppFormField, AppFormGrid } from "../layout";
import {
  applyOptionFieldChange,
  applyOptionFieldsChange,
  type WidgetChartMapping,
} from "../../utils/chartMappingBuilder";
import type { ChartOptionFieldDef } from "../../utils/chartOptionRegistry";
import { getOptionPath } from "../../utils/chartOptionPaths";
import ChartOptionFieldShell from "./ChartOptionFieldShell";
import ChartOptionSwitchField from "./ChartOptionSwitchField";
import ChartPlacementSelects from "./ChartPlacementSelects";
import {
  chartOptionFormGridSx,
  chartOptionOutlinedInputSx,
  chartOptionSectionLabelSx,
  GRID_HALF,
} from "../../lib/ui/formLayout";
import { useAppCustomStyles } from "../../assets/theme/custom/hooks";
import {
  normalizePlacementH,
  normalizePlacementV,
  type PlacementH,
  type PlacementV,
} from "./chartOptionLayout";

type Props = {
  fields: ChartOptionFieldDef[];
  mapping: WidgetChartMapping;
  disabled?: boolean;
  onChange: (next: WidgetChartMapping) => void;
};

function fieldById(fields: ChartOptionFieldDef[], id: string) {
  return fields.find((field) => field.id === id);
}

type SelectOption = { value: string; label: string };

function useSelectOptions(
  field: ChartOptionFieldDef | undefined,
  t: (key: string) => string
): SelectOption[] {
  return useMemo(
    () =>
      (field?.options ?? []).map((opt) => ({
        value: String(opt.value),
        label: t(opt.labelKey),
      })),
    [field?.options, t]
  );
}

export default function TitleLegendPanel({ fields, mapping, disabled, onChange }: Props) {
  const custom = useAppCustomStyles();
  const { t } = useTranslation();

  const titleTextField = fieldById(fields, "titleText");
  const titleSubtextField = fieldById(fields, "titleSubtext");
  const legendShowField = fieldById(fields, "legendShow");
  const legendOrientField = fieldById(fields, "legendOrient");
  const legendTypeField = fieldById(fields, "legendType");

  const legendOrientOptions = useSelectOptions(legendOrientField, t);
  const legendTypeOptions = useSelectOptions(legendTypeField, t);

  const legendVisible = Boolean(getOptionPath(mapping.optionTemplate, "legend.show") ?? true);

  const titleHorizontal = normalizePlacementH(getOptionPath(mapping.optionTemplate, "title.left"));
  const titleVertical = normalizePlacementV(getOptionPath(mapping.optionTemplate, "title.top"));
  const legendHorizontal = normalizePlacementH(getOptionPath(mapping.optionTemplate, "legend.left"));
  const legendVertical = normalizePlacementV(getOptionPath(mapping.optionTemplate, "legend.top"));

  const applyPlacement = (prefix: "title" | "legend", horizontal: PlacementH, vertical: PlacementV) => {
    onChange(
      applyOptionFieldsChange(mapping, [
        { path: `${prefix}.left`, value: horizontal },
        { path: `${prefix}.top`, value: vertical },
      ])
    );
  };

  return (
    <Box>
      <Typography variant="overline" sx={chartOptionSectionLabelSx}>
        {t("chartOptions.sections.title")}
      </Typography>
      <AppFormGrid spacing="compact" alignItems="stretch" sx={chartOptionFormGridSx}>
        {titleTextField && (
          <AppFormField size={GRID_HALF} align="stretch">
            <ChartOptionFieldShell
              label={t(titleTextField.labelKey)}
              helperText={titleTextField.hintKey ? t(titleTextField.hintKey) : undefined}
              disabled={disabled}
            >
              <AppTextField
                hiddenLabel
                label={t(titleTextField.labelKey)}
                margin="none"
                value={String(getOptionPath(mapping.optionTemplate, titleTextField.path) ?? "")}
                disabled={disabled}
                onChange={(e) =>
                  onChange(applyOptionFieldChange(mapping, titleTextField.path, e.target.value))
                }
                sx={chartOptionOutlinedInputSx}
              />
            </ChartOptionFieldShell>
          </AppFormField>
        )}
        {titleSubtextField && (
          <AppFormField size={GRID_HALF} align="stretch">
            <ChartOptionFieldShell label={t(titleSubtextField.labelKey)} disabled={disabled}>
              <AppTextField
                hiddenLabel
                label={t(titleSubtextField.labelKey)}
                margin="none"
                value={String(getOptionPath(mapping.optionTemplate, titleSubtextField.path) ?? "")}
                disabled={disabled}
                onChange={(e) =>
                  onChange(applyOptionFieldChange(mapping, titleSubtextField.path, e.target.value))
                }
                sx={chartOptionOutlinedInputSx}
              />
            </ChartOptionFieldShell>
          </AppFormField>
        )}
        <ChartPlacementSelects
          horizontal={titleHorizontal}
          vertical={titleVertical}
          horizontalLabelKey="chartOptions.fields.titleLeft"
          verticalLabelKey="chartOptions.fields.titleTop"
          disabled={disabled}
          onChange={({ horizontal, vertical }) => applyPlacement("title", horizontal, vertical)}
        />
      </AppFormGrid>

      {(legendShowField || legendOrientField || legendTypeField) && (
        <>
          <Divider sx={custom.components.chartOptionPanels.divider} />
          <Typography variant="overline" sx={chartOptionSectionLabelSx}>
            {t("chartOptions.sections.legend")}
          </Typography>
          <AppFormGrid spacing="compact" alignItems="stretch" sx={chartOptionFormGridSx}>
            {legendShowField && (
              <AppFormField size={GRID_HALF} align="stretch">
                <ChartOptionSwitchField
                  label={t(legendShowField.labelKey)}
                  checked={legendVisible}
                  disabled={disabled}
                  onChange={(_, checked) =>
                    onChange(applyOptionFieldChange(mapping, legendShowField.path, checked))
                  }
                />
              </AppFormField>
            )}
            {legendOrientField && legendVisible && legendOrientField.options && (
              <AppFormField size={GRID_HALF} align="stretch">
                <ChartOptionFieldShell label={t(legendOrientField.labelKey)} disabled={disabled}>
                  <AppAutocomplete<SelectOption>
                    label={t(legendOrientField.labelKey)}
                    options={legendOrientOptions}
                    value={
                      legendOrientOptions.find(
                        (o) =>
                          o.value === String(getOptionPath(mapping.optionTemplate, legendOrientField.path) ?? "")
                      ) ?? null
                    }
                    disabled={disabled}
                    onChange={(_, opt) =>
                      opt &&
                      onChange(applyOptionFieldChange(mapping, legendOrientField.path, opt.value))
                    }
                    getOptionLabel={(o) => o.label}
                    isOptionEqualToValue={(a, b) => a.value === b.value}
                    disableClearable
                    renderInput={(params) => (
                      <AppTextField
                        {...params}
                        hiddenLabel
                        label={t(legendOrientField.labelKey)}
                        margin="none"
                        sx={chartOptionOutlinedInputSx}
                      />
                    )}
                    sx={chartOptionOutlinedInputSx}
                  />
                </ChartOptionFieldShell>
              </AppFormField>
            )}
            {legendTypeField && legendVisible && legendTypeField.options && (
              <AppFormField size={GRID_HALF} align="stretch">
                <ChartOptionFieldShell label={t(legendTypeField.labelKey)} disabled={disabled}>
                  <AppAutocomplete<SelectOption>
                    label={t(legendTypeField.labelKey)}
                    options={legendTypeOptions}
                    value={
                      legendTypeOptions.find(
                        (o) =>
                          o.value === String(getOptionPath(mapping.optionTemplate, legendTypeField.path) ?? "")
                      ) ?? null
                    }
                    disabled={disabled}
                    onChange={(_, opt) =>
                      opt &&
                      onChange(applyOptionFieldChange(mapping, legendTypeField.path, opt.value))
                    }
                    getOptionLabel={(o) => o.label}
                    isOptionEqualToValue={(a, b) => a.value === b.value}
                    disableClearable
                    renderInput={(params) => (
                      <AppTextField
                        {...params}
                        hiddenLabel
                        label={t(legendTypeField.labelKey)}
                        margin="none"
                        sx={chartOptionOutlinedInputSx}
                      />
                    )}
                    sx={chartOptionOutlinedInputSx}
                  />
                </ChartOptionFieldShell>
              </AppFormField>
            )}
            {legendVisible && (
              <ChartPlacementSelects
                horizontal={legendHorizontal}
                vertical={legendVertical}
                horizontalLabelKey="chartOptions.fields.legendLeft"
                verticalLabelKey="chartOptions.fields.legendTop"
                disabled={disabled}
                onChange={({ horizontal, vertical }) => applyPlacement("legend", horizontal, vertical)}
              />
            )}
          </AppFormGrid>
        </>
      )}
    </Box>
  );
}
